// useOmnibarChord — the ONE keyboard chord the web build registers: ⌘K (Ctrl+K
// off mac) opens the omnibar.
//
// Chords are otherwise DESKTOP-ONLY, and the desktop shell is gone: the web
// build registers ZERO chrome-level keyboard shortcuts, so it never claims or
// `preventDefault`s a chord that would overshadow the browser's own (Cmd+T /
// Cmd+W / Cmd+L / Cmd+1–9 …). Cmd+K is the single ratified exception (CLAUDE.md;
// IDD 5320d96f, amended by dfd31976): the chat-primary shell hid the top strip,
// so the omnibar has no other web entry, and Cmd+K is safe to claim because it
// is not browser-reserved.
//
// This replaces the former five-listener `useChords` router (desktop menu
// channel, ⌥-slot accelerators, tab/pane/history chords, and the catalogue-
// projected chrome chords) — all of which were inert or desktop-gated on web.
// Only the ⌘K → `open-palette` branch was ever live in the browser, so it is all
// that remains, as a self-contained listener with no dependency on the removed
// chord catalogue, matcher, or override layer.

import { useEffect } from "react";
import type { KbdPlatform } from "../../components/Kbd/Kbd.js";
import type { TriggerSignal } from "../trigger.js";

export type OmnibarChordDeps = {
    // The ONE stamped door — the shell stamps the clock and routes. The ⌘K press
    // fires the `open-palette` UiIntent through it, exactly as its on-screen
    // omnibar affordance does.
    readonly applyTrigger: (signal: TriggerSignal) => void;
    // Which platform's primary modifier opens the omnibar (⌘ on mac, Ctrl else).
    readonly kbdPlatform: KbdPlatform;
};

export const useOmnibarChord = ({
    applyTrigger,
    kbdPlatform,
}: OmnibarChordDeps): void => {
    // No dependency array on purpose: re-subscribing every render keeps the
    // closed-over `applyTrigger` fresh, matching the former listener's timing.
    useEffect(() => {
        const mac = kbdPlatform === "mac";
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.altKey || e.shiftKey) return;
            // Primary modifier only: ⌘ on mac (and NOT a literal Ctrl held too,
            // which would be a different chord), Ctrl elsewhere.
            const mod = mac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
            if (!mod) return;
            if (e.key.toLowerCase() !== "k") return;
            // Claim ⌘K and open the omnibar via the stamped door. A mod-chord
            // types no character, so no focus guard is needed (it fires from an
            // input too, matching the omnibar's on-screen door).
            e.preventDefault();
            applyTrigger({
                provenance: { type: "user" },
                action: { type: "key", action: { type: "open-palette" } },
            });
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    });
};
